import React, { useState } from 'react';
import { useGetEmailTester } from '@openapi/logs/logs';
import useNotify from '../../hooks/useNotify';
import { useTranslation } from 'react-i18next';
import PageWithTitle from '../../components/atom/PageWithTitle';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    CircularProgress,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import SmartphoneIcon from '@mui/icons-material/Smartphone';

const DEVICE_STORAGE_KEY = 'email-tester-device';

// Keep in sync with prettifyOptionLabel in backend/src/email-tester.js — overrides
// for template names whose auto-titlecased form reads awkwardly.
const TEMPLATE_LABEL_OVERRIDES = {
    existinguserinvite: 'Existing User Invite',
    pwreset: 'Password Reset',
    checkin_closed_stats: 'Check-In Closed Stats',
    admin_refund_notify: 'Admin Refund Notification',
    booking_update_notify: 'Booking Update Notification',
    delete_timeslot: 'iCal Sync Result',
};

const titleCaseSegment = (segment) =>
    segment.replaceAll(/[_-]+/g, ' ').replaceAll(/\b\w/g, (c) => c.toUpperCase());

const prettifyBaseName = (name) => TEMPLATE_LABEL_OVERRIDES[name] || titleCaseSegment(name);

// Group a flat list of "base" and "base.variant" template names into a map
// keyed by base, where each value lists the variant suffixes (empty string
// means the default fixture without a variant).
const groupTemplatesByBase = (names) => {
    const map = {};
    for (const name of names) {
        const dotIdx = name.indexOf('.');
        const baseName = dotIdx === -1 ? name : name.substring(0, dotIdx);
        const variant = dotIdx === -1 ? '' : name.substring(dotIdx + 1);
        if (!map[baseName]) map[baseName] = [];
        map[baseName].push(variant);
    }
    for (const baseName of Object.keys(map)) {
        // Sort so "" (default) is always first and named variants follow alphabetically.
        map[baseName].sort((a, b) => {
            if (a === b) return 0;
            if (a === '') return -1;
            if (b === '') return 1;
            return a.localeCompare(b);
        });
    }
    return map;
};

const combineTemplateName = (baseName, variant) => (variant ? `${baseName}.${variant}` : baseName);

// Note: If you want to remove the phone/browser styles later and return to a plain box,
// simply clear out these two objects.
const mobileFrameStyles = {
    position: 'relative',
    boxSizing: 'content-box',
    width: 320,
    maxWidth: '100%',
    height: 480,
    maxHeight: '70vh',
    border: '10px solid black',
    borderTop: '80px solid black',
    borderBottom: '85px solid black',
    borderRadius: '30px',
    boxShadow: '0 10px 15px -6px rgba(32, 32, 32, .8), 0 0 0 3px #ccc',
    '&::before': {
        content: '""',
        width: '60px',
        height: '8px',
        backgroundColor: '#ccc',
        position: 'absolute',
        top: '-44px',
        left: '50%',
        marginLeft: '-30px',
        display: 'block',
        zIndex: 1,
        borderRadius: '5px',
        boxShadow: '0 1px 3px 1px #444, inset 0 0 10px 3px black',
    },
    '&::after': {
        content: '"\\25A1"',
        textAlign: 'center',
        color: '#666',
        fontSize: '50px',
        lineHeight: '55px',
        width: '55px',
        height: '55px',
        backgroundColor: '#222',
        position: 'absolute',
        bottom: '-70px',
        left: '50%',
        marginLeft: '-27.5px',
        display: 'block',
        zIndex: 1,
        borderRadius: '50%',
        boxShadow: '0 1px 3px 1px #444, inset 0 0 10px 3px black',
    },
};

const desktopFrameStyles = {
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
    height: 'calc(100vh - 250px)',
    borderRadius: '12px',
    border: '1px solid #e0e0e0',
    boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.1)',
    pt: '40px',
    boxSizing: 'border-box',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '40px',
        backgroundColor: '#f6f6f6',
        borderBottom: '1px solid #e0e0e0',
        zIndex: 10,
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        top: '14px',
        left: '16px',
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        backgroundColor: '#ff5f56',
        boxShadow: '20px 0 0 #ffbd2e, 40px 0 0 #27c93f',
        zIndex: 11,
    },
};

const EmailTesterPage = () => {
    const { t } = useTranslation();
    const { notifyError } = useNotify();
    const [selectedBase, setSelectedBase] = useState('');
    const [selectedVariant, setSelectedVariant] = useState('');
    const [selectedLang, setSelectedLang] = useState('en');
    const [device, setDevice] = useState(
        () => localStorage.getItem(DEVICE_STORAGE_KEY) || 'desktop',
    );

    const handleDeviceChange = (_, v) => {
        if (!v) return;
        setDevice(v);
        localStorage.setItem(DEVICE_STORAGE_KEY, v);
    };

    const { data: metadata, isLoading: loadingMetadata } = useGetEmailTester(
        {},
        { query: { onError: notifyError } },
    );

    const templatesByBase = groupTemplatesByBase(metadata?.templates || []);
    const baseNames = Object.keys(templatesByBase).sort();
    const baseName = selectedBase || baseNames[0] || '';
    const variantsForBase = templatesByBase[baseName] || [''];
    const variant = variantsForBase.includes(selectedVariant) ? selectedVariant : '';
    const template = baseName ? combineTemplateName(baseName, variant) : '';

    const { data: previewData, isLoading: loadingPreview } = useGetEmailTester(
        template ? { template, lang: selectedLang } : undefined,
        { query: { enabled: !!template, onError: notifyError } },
    );

    const data = previewData ?? metadata;
    const isLoading = loadingMetadata || loadingPreview;

    return (
        <PageWithTitle title={t('emaillogs.emailTester')}>
            <Stack spacing={2} sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 250 }}>
                        <InputLabel>{t('emaillogs.emailTesterTemplate')}</InputLabel>
                        <Select
                            value={baseName}
                            label={t('emaillogs.emailTesterTemplate')}
                            onChange={(e) => {
                                setSelectedBase(e.target.value);
                                setSelectedVariant('');
                            }}
                            disabled={isLoading}
                        >
                            {baseNames.map((name) => (
                                <MenuItem key={name} value={name}>
                                    {prettifyBaseName(name)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl
                        size="small"
                        sx={{ minWidth: 180 }}
                        disabled={isLoading || variantsForBase.length <= 1}
                    >
                        <InputLabel>{t('emaillogs.emailTesterVariant')}</InputLabel>
                        <Select
                            value={variant}
                            label={t('emaillogs.emailTesterVariant')}
                            onChange={(e) => setSelectedVariant(e.target.value)}
                        >
                            {variantsForBase.map((v) => (
                                <MenuItem key={v || 'default'} value={v}>
                                    {v
                                        ? titleCaseSegment(v)
                                        : t('emaillogs.emailTesterVariantDefault')}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel>{t('emaillogs.emailTesterLanguage')}</InputLabel>
                        <Select
                            value={selectedLang}
                            label={t('emaillogs.emailTesterLanguage')}
                            onChange={(e) => setSelectedLang(e.target.value)}
                            disabled={isLoading}
                        >
                            {(data?.languages || []).map((lang) => (
                                <MenuItem key={lang} value={lang}>
                                    {lang.toUpperCase()}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <ToggleButtonGroup
                        value={device}
                        exclusive
                        onChange={handleDeviceChange}
                        size="small"
                        sx={{ alignSelf: 'center' }}
                    >
                        <ToggleButton
                            value="desktop"
                            aria-label={t('emaillogs.emailTesterDesktop')}
                        >
                            <DesktopWindowsIcon />
                        </ToggleButton>
                        <ToggleButton value="mobile" aria-label={t('emaillogs.emailTesterMobile')}>
                            <SmartphoneIcon />
                        </ToggleButton>
                    </ToggleButtonGroup>
                    {data?.html && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ alignSelf: 'center' }}
                        >
                            {device === 'mobile' ? '320 × 480' : '1024 × 768'}
                        </Typography>
                    )}
                    {isLoading && <CircularProgress size={32} />}
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        width: '100%',
                        minHeight: 'calc(100vh - 250px)',

                        px: device === 'mobile' ? 2 : 0,
                    }}
                >
                    <Box
                        sx={{
                            bgcolor: 'white',
                            ...(device === 'mobile' ? mobileFrameStyles : desktopFrameStyles),
                        }}
                    >
                        {data?.html ? (
                            <iframe
                                title="Email preview"
                                srcDoc={data.html}
                                style={{ width: '100%', height: '100%', border: 'none' }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    minHeight: device === 'mobile' ? 667 : 400,
                                    color: 'text.secondary',
                                }}
                            >
                                {isLoading
                                    ? t('common.loading')
                                    : t('emaillogs.emailTesterSelectTemplate')}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Stack>
        </PageWithTitle>
    );
};

export default EmailTesterPage;
